/**
 * Utility functions for transforming Software data to Cytoscape format
 */

/**
 * Get health color based on score
 * @param {number} score - Health score (0-100)
 * @returns {string} Hex color code
 */
export const getHealthColor = (score) => {
  if (score >= 80) return '#10b981'; // green - healthy
  if (score >= 60) return '#eab308'; // yellow - degraded
  if (score >= 30) return '#f97316'; // orange - warning
  return '#ef4444'; // red - critical
};

/**
 * Transform Software data format to Cytoscape elements format
 * @param {Array} data - Array of software change objects
 * @returns {Object} Object containing nodes and edges arrays
 */
export const transformDataToCytoscape = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { nodes: [], edges: [] };
  }

  const nodes = [];
  const edges = [];
  const nodeMap = new Set();
  const edgeMap = new Set(); // Track unique edges
  
  // Limit to top 50 changes by health score
  const limitedData = data
    .filter(item => item && item.changeNumber) // Filter out invalid items
    .sort((a, b) => (b.healthScore || 0) - (a.healthScore || 0))
    .slice(0, 50);
  
  // First pass: Create all nodes
  limitedData.forEach((item) => {
    const mainNodeId = item.changeNumber;
    if (!nodeMap.has(mainNodeId)) {
      nodes.push({
        data: {
          id: mainNodeId,
          label: mainNodeId,
          description: item.description || 'Unknown',
          health: item.health || 'UNKNOWN',
          healthScore: item.healthScore || 0,
          isRoot: true,
          type: 'CHANGE'
        }
      });
      nodeMap.add(mainNodeId);
    }
    
    // Add upstream dependency nodes
    const upstream = item.raw_data?.relationships?.upstream || [];
    upstream.forEach((dep, idx) => {
      const depId = dep.entityId || `${mainNodeId}-up-${idx}`;
      if (!nodeMap.has(depId)) {
        nodes.push({
          data: {
            id: depId,
            label: dep.displayName || depId,
            type: dep.entityType || 'UNKNOWN',
            direction: 'upstream',
            healthScore: dep.health?.score || 50,
            healthStatus: dep.health?.status || 'UNKNOWN'
          }
        });
        nodeMap.add(depId);
      }
    });
    
    // Add downstream dependency nodes
    const downstream = item.raw_data?.relationships?.downstream || [];
    downstream.forEach((dep, idx) => {
      const depId = dep.entityId || `${mainNodeId}-down-${idx}`;
      if (!nodeMap.has(depId)) {
        nodes.push({
          data: {
            id: depId,
            label: dep.displayName || depId,
            type: dep.entityType || 'UNKNOWN',
            direction: 'downstream',
            healthScore: dep.health?.score || 50,
            healthStatus: dep.health?.status || 'UNKNOWN'
          }
        });
        nodeMap.add(depId);
      }
    });
  });
  
  // Second pass: Create edges between existing nodes
  let edgeCounter = 0;
  limitedData.forEach((item) => {
    const mainNodeId = item.changeNumber;
    
    // Create upstream edges
    const upstream = item.raw_data?.relationships?.upstream || [];
    upstream.forEach((dep, idx) => {
      const depId = dep.entityId || `${mainNodeId}-up-${idx}`;
      const edgeKey = `${depId}->${mainNodeId}`;
      if (!edgeMap.has(edgeKey) && nodeMap.has(depId) && nodeMap.has(mainNodeId)) {
        edges.push({
          data: {
            id: `edge-${edgeCounter++}`,
            source: depId,
            target: mainNodeId,
            direction: 'upstream'
          }
        });
        edgeMap.add(edgeKey);
      }
    });
    
    // Create downstream edges
    const downstream = item.raw_data?.relationships?.downstream || [];
    downstream.forEach((dep, idx) => {
      const depId = dep.entityId || `${mainNodeId}-down-${idx}`;
      const edgeKey = `${mainNodeId}->${depId}`;
      if (!edgeMap.has(edgeKey) && nodeMap.has(mainNodeId) && nodeMap.has(depId)) {
        edges.push({
          data: {
            id: `edge-${edgeCounter++}`,
            source: mainNodeId,
            target: depId,
            direction: 'downstream'
          }
        });
        edgeMap.add(edgeKey);
      }
    });
    
    // Also try to connect changes to each other if they reference each other
    upstream.forEach((dep) => {
      if (dep.entityId && nodeMap.has(dep.entityId)) {
        const edgeKey = `${dep.entityId}->${mainNodeId}`;
        if (!edgeMap.has(edgeKey)) {
          edges.push({
            data: {
              id: `edge-${edgeCounter++}`,
              source: dep.entityId,
              target: mainNodeId,
              direction: 'upstream'
            }
          });
          edgeMap.add(edgeKey);
        }
      }
    });
    
    downstream.forEach((dep) => {
      if (dep.entityId && nodeMap.has(dep.entityId)) {
        const edgeKey = `${mainNodeId}->${dep.entityId}`;
        if (!edgeMap.has(edgeKey)) {
          edges.push({
            data: {
              id: `edge-${edgeCounter++}`,
              source: mainNodeId,
              target: dep.entityId,
              direction: 'downstream'
            }
          });
          edgeMap.add(edgeKey);
        }
      }
    });
  });
  
  console.log('[cytoscapeTransform] Generated nodes:', nodes.length);
  console.log('[cytoscapeTransform] Generated edges:', edges.length);
  console.log('[cytoscapeTransform] Sample edges:', edges.slice(0, 3));
  console.log('[cytoscapeTransform] Sample nodes:', nodes.slice(0, 3).map(n => n.data.id));
  
  // Validate edges reference existing nodes
  const invalidEdges = edges.filter(e => 
    !nodeMap.has(e.data.source) || !nodeMap.has(e.data.target)
  );
  if (invalidEdges.length > 0) {
    console.warn('[cytoscapeTransform] Found invalid edges:', invalidEdges.length);
    console.warn('[cytoscapeTransform] Sample invalid edge:', invalidEdges[0]);
  }
  
  return { nodes, edges };
};
